import { Injectable } from '@angular/core';
import { IMessages } from '@shared/interfaces/messages';
import { Observable, map, BehaviorSubject, Subject, tap, finalize, distinctUntilChanged, take, delay } from 'rxjs';
import { IQuery } from '@shared/interfaces/query';
import { NotificationsCenterService } from '@app/admin-station/notifications-center/services/notifications-center.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SignalrService, SIGNALR_CHANNEL } from '@app/shared/services/signalr/signalr.service';
import { NotificationOptions } from '@app/main/enums/notification-options.enum';
import { SignalrEvent } from '../enums/signalr-event';
import { UserService } from '@app/admin-station/work-center/user/services/user.service';
@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class NotificationService {
  private _notifications$: BehaviorSubject<IMessages[]>;
  private _unreadNotifications$: BehaviorSubject<IMessages[]>;
  private _notificationCounter$ = new BehaviorSubject<number>(0);
  private _showSpinner$ = new BehaviorSubject<boolean>(false);
  private QUERY: IQuery = { sort: '-createdDate', pageSize: 30 };
  private notificationsPage: number;
  private unreadNotificationsPage: number;
  public refreshNotifications = new Subject<void>();
  public notificationChipOption: string;

  constructor(
    private notificationsCenterService: NotificationsCenterService,
    private signalrService: SignalrService,
    private _userService: UserService
  ) {
    this._unreadNotifications$ = new BehaviorSubject([]);
    this._notifications$ = new BehaviorSubject([]);
    this.notificationsPage = 1;
    this.unreadNotificationsPage = 1;
    this.notifications = [];
    this.unreadNotifications = [];

    this.refreshNotifications.pipe(untilDestroyed(this)).subscribe(() => {
      this.loadNotifications();
    });

    this._userService.userLoginData$
      .pipe(
        distinctUntilChanged(),
        untilDestroyed(this),
        tap((userData) => {
          if (!userData) return;
          this.loadNotifications();
          this.initSignalR();
        })
      )
      .subscribe();
  }

  initSignalR() {
    if (this.signalrService?.hubConnections && this.signalrService.hubConnections[SIGNALR_CHANNEL]) {
      this.listenSignalrEvent();
    } else {
      this.signalrService.channelCreated$.pipe(untilDestroyed(this)).subscribe(() => {
        this.listenSignalrEvent();
      });
    }
  }

  private listenSignalrEvent(): void {
    this.signalrService.hubConnections[SIGNALR_CHANNEL].on(SignalrEvent.NotificationAdded, () => {
      this.loadNotifications();
    });
  }

  private loadNotifications(nextPage?: boolean): void {
    if (!nextPage) {
      this.loadNotificationsCounter();
    }

    if (this.notificationChipOption === NotificationOptions.UNREAD || !nextPage) {
      this.loadUnreadNotifications(nextPage);
    }

    if (this.notificationChipOption === NotificationOptions.ALL || !nextPage) {
      this.loadInboxNotifications(nextPage);
    }
  }

  private loadInboxNotifications(nextPage: boolean): void {
    this.showSpinner = true;
    this.getMessages(null, nextPage)
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.showSpinner = false;
        })
      )
      .subscribe((notifications) => {
        if (nextPage) {
          this.notifications$.pipe(take(1)).subscribe((currentNotifications) => {
            this.notifications = [...currentNotifications, ...notifications];
          });
        } else {
          this.notifications = notifications;
        }
      });
  }

  private loadUnreadNotifications(nextPage: boolean): void {
    this.showSpinner = true;
    this.getUnreadMessages(null, nextPage)
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.showSpinner = false;
        })
      )
      .subscribe((notifications) => {
        if (nextPage) {
          this.unreadNotifications$.pipe(take(1)).subscribe((currentNotifications) => {
            this.unreadNotifications = [...currentNotifications, ...notifications];
          });
        } else {
          this.unreadNotifications = notifications;
        }
      });
  }

  private loadNotificationsCounter(): void {
    this.getUnreadMessagesCount()
      .pipe(untilDestroyed(this))
      .subscribe((count) => {
        this.notificationCounter = count;
      });
  }

  get notificationCounter$(): Observable<number> {
    return this._notificationCounter$.asObservable();
  }

  set notificationCounter(counter: number) {
    this._notificationCounter$.next(counter);
  }

  get notifications$(): Observable<IMessages[]> {
    return this._notifications$.asObservable();
  }

  set notifications(messages: IMessages[]) {
    this._notifications$.next(messages);
  }

  get unreadNotifications$(): Observable<IMessages[]> {
    return this._unreadNotifications$.asObservable();
  }

  set unreadNotifications(messages: IMessages[]) {
    this._unreadNotifications$.next(messages);
  }

  get showSpinner$(): Observable<boolean> {
    return this._showSpinner$.asObservable();
  }

  set showSpinner(value: boolean) {
    this._showSpinner$.next(value);
  }

  public getMessages(query: IQuery = null, nextPage?: boolean): Observable<IMessages[]> {
    delete this.QUERY?.filter;
    const queryOptions = query || this.QUERY;

    if (nextPage) {
      this.notificationsPage++;
      queryOptions.pageIndex = this.notificationsPage;
    } else {
      queryOptions.pageIndex = 1;
      this.notificationsPage = 1;
    }

    return this.notificationsCenterService.getInboxMessages(queryOptions).pipe(map((data: any) => data.result));
  }

  public getUnreadMessages(query: IQuery = null, nextPage?: boolean): Observable<IMessages[]> {
    delete this.QUERY?.filter;
    const queryOptions = query || this.QUERY;

    if (nextPage) {
      this.unreadNotificationsPage++;
      queryOptions.pageIndex = this.unreadNotificationsPage;
    } else {
      queryOptions.pageIndex = 1;
      this.unreadNotificationsPage = 1;
    }

    return this.notificationsCenterService.getUnreadMessages(queryOptions).pipe(map((data: any) => data.result));
  }

  public getUnreadMessagesCount(): Observable<number> {
    return this.notificationsCenterService.getUnreadMessagesCount().pipe(map((data: any) => data || 0));
  }
}
